import { useEffect, useRef, useCallback } from 'react';
import { getToken, removeToken } from '../helpers/tokenStorage';
import { purgeStoredState } from 'redux-persist';
import {
  persistAuthConfig,
  persistUserConfig,
  persistHandoverConfig,
  persistHandoverTaskConfig,
  persistHandoverCommentConfig,
} from '../store/persistConfigs';
import { throttle } from 'lodash';
import { projectConfig } from '../config/projectConfig';
import { firebaseAuth } from '../config/firebaseClient';
import { signOut as signOutFirebaseUser } from 'firebase/auth';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const CHECK_INTERVAL = 60 * 1000; // Check every minute

export const useIdleTimer = () => {
  const lastActivityRef = useRef<number>(Date.now());

  const logout = async () => {
    console.log('[IdleTimer] User inactive for 30 minutes. Logging out...');
    try {
      const token = getToken();
      await fetch(`${projectConfig.apiEndpoints.auth}/logout`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch (error) {
      console.error('[IdleTimer] Logout request failed:', error);
    }
    try {
      await signOutFirebaseUser(firebaseAuth);
    } catch (error) {
      console.error('[IdleTimer] Firebase client logout failed:', error);
    }
    removeToken();
    await purgeStoredState(persistAuthConfig);
    await purgeStoredState(persistUserConfig);
    await purgeStoredState(persistHandoverConfig);
    await purgeStoredState(persistHandoverTaskConfig);
    await purgeStoredState(persistHandoverCommentConfig);
    window.location.href = '/';
  };

  // Throttle activity updates to once per second to avoid performance issues
  const updateActivity = useCallback(
    throttle(() => {
      lastActivityRef.current = Date.now();
    }, 1000),
    []
  );

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check for inactivity periodically
    const intervalId = setInterval(() => {
      const token = getToken();
      const userAuth = sessionStorage.getItem('persist:auth') || localStorage.getItem('persist:auth');
      if (token || userAuth) {
        const now = Date.now();
        if (now - lastActivityRef.current >= IDLE_TIMEOUT) {
          logout();
        }
      }
    }, CHECK_INTERVAL);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
      updateActivity.cancel();
    };
  }, [updateActivity]);
};
